import {
  Button,
  FileUpload,
  Icon,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  TableColumn,
} from '@components';
import { usePermissionContext } from '@contexts/PermissionProvider';
import { useModal } from '@hooks';
import TransactionAPICaller, {
  TransactionFilters,
} from '@services/api/transaction';
import CategoryAPICaller, { CategoryFilters } from '@services/api/category';
import CostCenterAPICaller from '@services/api/costCenter';
import { TransactionFile } from 'interfaces/TransactionFile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Category } from 'interfaces/Category';
import { CostCenter } from 'interfaces/CostCenter';
import { toast } from 'react-toastify';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import { encodeDate, parseDate } from 'utils/string/dates';
import CostcenterFromToApiCaller from '@services/api/costCenterFromTo';
import { CostCenterFromTo } from 'interfaces/CostCenterFromTo';
import UploadFileCaller from '@services/api/upload-file';
import { convertJsonToXls } from '@services/io/file';

const options = [
  { label: 'Receita', value: 'income' },
  { label: 'Despesa', value: 'expense' },
];

const getValue = (value: 'income' | 'expense') => {
  return options.find((option) => option.value === value);
};

const useFetchCostCenterFromTo = () => {
  const [costCentersFromTo, setCostCentersFromTo] = useState<
    Array<CostCenterFromTo>
  >([]);
  const [fetchingCostCentersFromTo, setFetchingCostCentersFromTo] =
    useState(false);
  const loadCostCentersFromTo = useCallback(async () => {
    setFetchingCostCentersFromTo(true);
    const values = await CostcenterFromToApiCaller.listAll();
    setCostCentersFromTo(values.result);
    setFetchingCostCentersFromTo(false);
    return;
  }, []);

  return {
    costCentersFromTo,
    loadCostCentersFromTo,
    fetchingCostCentersFromTo,
  };
};

export default function TransactionPage() {
  const {
    watch,
    register,
    setValue,
    setError,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const { isOpen, toggle: toggleTransactionModal } = useModal();
  const { isOpen: uploadFileIsOpen, toggle: uploadFileIsOpenToggle } =
    useModal();
  const [type, setType] = useState<{
    label: string;
    value: string;
  }>();

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const [result, setResult] = useState<any>();

  const { hasPermission } = usePermissionContext();

  const confirmation = useConfirmationModal();
  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<TransactionFile>;
  }>({
    total: 0,
    result: [],
  });

  const [categories, setCategories] = useState<Array<Category>>([]);
  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([]);

  const [filters, setFilters] = useState<
    Omit<TransactionFilters, 'type'> & { type: 'income' | 'expense' }
  >({
    type: 'income',
  });

  const categoriesPageSize = 50;

  const {
    costCentersFromTo,
    loadCostCentersFromTo,
    fetchingCostCentersFromTo,
  } = useFetchCostCenterFromTo();

  const handleUpload = async () => {
    setUploadError(false);
    setUploading(true);
    setResult(undefined);

    UploadFileCaller.uploadFile({
      file,
      type: type?.value,
    })
      .then((response) => {
        setResult(response.result);
        setUploadError(false);

        convertJsonToXls(
          response.result.nonImportedTransactions,
          `${type?.label}-nao-importadas.xlsx`
        );

        setUploading(false);
      })
      .catch(() => {
        setResult(undefined);
        setUploading(false);
        setUploadError(true);
      });
  };

  const list = (filters: TransactionFilters) => {
    setIsLoading(true);
    TransactionAPICaller.list(filters)
      .then((values) => {
        setTableData(values);
      })
      .finally(() => setIsLoading(false));
  };

  const loadCostCenters = useCallback(() => {
    if (hasPermission('costCenters', 'read')) {
      CostCenterAPICaller.listAll().then((values) => {
        setCostCenters(values.result);
      });
    }
  }, [hasPermission]);

  const loadCategories = async (categoryFilters: CategoryFilters) => {
    await CategoryAPICaller.list(categoryFilters).then((values) => {
      setCategories(values.result);
    });
  };

  useEffect(() => {
    loadCostCenters();
  }, [loadCostCenters]);

  useEffect(() => {
    list(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((currentFilters) => ({ ...currentFilters, page: 0 }));
  }, [setFilters]);

  const openEditModal = async (editData?: string) => {
    reset();
    if (editData) {
      const transaction = await TransactionAPICaller.fetch(editData);
      await loadCategories({
        costCenterId: transaction.category.costCenterId,
        size: categoriesPageSize,
      });

      setValue('id', transaction.id);
      setValue('type', transaction.type);
      setValue('businessPartner', transaction.businessPartner);
      setValue('description', transaction.description);
      setValue('amount', transaction.amount);
      // Textual date extraction (same as the table column): timezone-aware
      // parsing (parseISO) renders the previous day for UTC-negative zones,
      // and saving would then rewrite the stored date one day back.
      setValue('paymentDate', parseDate(transaction.paymentDate));
      setValue('costCenterFromToId', transaction.costCenterFromToId);
      setValue('observation', transaction.observation);
      setValue('costCenterName', transaction.costCenter.name);
      setValue('costCenterId', transaction.costCenter.id);
      setValue('categoryName', transaction.category.name);
      setValue('categoryId', transaction.category.id);
    } else {
      loadCostCentersFromTo();
    }

    toggleTransactionModal();
  };

  const handleDelete = async (id: string) => {
    confirmation({
      title: 'Excluir transação',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir essa transação?',
      onSubmit: () =>
        TransactionAPICaller.remove(id).then((_) => list(filters)),
    });
  };

  const handleCreateOrUpdate = async (data: TransactionFile) => {
    TransactionAPICaller.createOrUpdate(data, setError)
      .then((res) => {
        if (res.data.errors) return;
        toggleTransactionModal();
        setFilters((filters) => ({ ...filters, type: data.type }));
      })
      .catch((error) => {
        const message =
          error.response.data.message || 'Erro ao cadastrar transação!';
        toast.error(message);
      });
  };

  const currentCostCenterFromTo = watch('costCenterFromToId');

  const costCenterValueLabel = useMemo(() => {
    const costCenterFromTo = costCentersFromTo.find(
      (costCenterFromTo) => costCenterFromTo.id === currentCostCenterFromTo
    );

    return costCenterFromTo
      ? `${costCenterFromTo.from} para ${costCenterFromTo.to.name}`
      : '';
  }, [currentCostCenterFromTo, costCentersFromTo]);

  const handleOpenUploadModal = () => {
    setUploadError(false);
    setUploading(false);
    setResult(undefined);
    setFile(undefined);
    setType(getValue(filters.type));
    uploadFileIsOpenToggle();
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Realizado" />

        <div className="mt-s-200">
          <Table
            data={tableData.result}
            totalRecords={tableData.total}
            showColumnSelector={false}
            filters={filters}
            setFilters={(newFilters) => {
              setFilters((prev) => ({ ...prev, ...newFilters }));
            }}
            withPagination
            isLoading={isLoading}
            withSearchInput
            additionalFilters={
              <div className="row ml-s-100 gap-x-2">
                <div className="col-sm-5">
                  <Select
                    value={getValue(filters.type)}
                    placeholder="Filtrar por Tipo"
                    options={options}
                    defaultValue={options[1]}
                    isLoading={fetchingCostCentersFromTo}
                    onSelect={(type: any) => {
                      setFilters((filters) => ({
                        ...filters,
                        page: 1,
                        type: type.value,
                      }));
                    }}
                  />
                </div>
                <div className="col-sm-7">
                  <div className="row">
                    <div className="col-sm-6">
                      <Button
                        onClick={() => {
                          openEditModal();
                        }}
                      >
                        Adicionar realizado
                      </Button>
                    </div>
                    <div className="col-sm-6">
                      {hasPermission('transactions') && (
                        <Button onClick={handleOpenUploadModal}>
                          Importar arquivo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }
          >
            <TableColumn
              fromKey="paymentDate"
              header="Data do pagamento"
              render={(paymentDate) => parseDate(paymentDate)}
            />
            <TableColumn
              fromKey="category"
              header="Categoria"
              render={(category) => category.name}
            />
            <TableColumn
              fromKey="category"
              header="Centro de custo"
              render={(category) => category.costCenter.name}
            />
            <TableColumn
              fromKey="costCenterFromTo"
              header="Centro de custo para"
              render={(costCenterFromTo) => costCenterFromTo?.to?.name}
            />
            <TableColumn fromKey="businessPartner" header="Fornecedor" />
            <TableColumn fromKey="description" header="Descrição" />
            <TableColumn fromKey="amount" header="Valor" />
            <TableColumn fromKey="observation" header="Observação" />
            <TableColumn
              fromKey="file"
              header="Nome do arquivo"
              render={(file) => file?.name}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    onClick={() => {
                      openEditModal(id);
                    }}
                  />
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Deletar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>delete</Icon>}
                    onClick={async () => {
                      handleDelete(id);
                    }}
                  />
                );
              }}
            />
          </Table>
        </div>
        <Modal
          size="large"
          isOpen={uploadFileIsOpen}
          onClickAway={uploadFileIsOpenToggle}
        >
          <div
            className="row mt-s-200 justify-center p-s-200"
            style={{
              border: '1px dashed #3F608E',
            }}
          >
            <FileUpload
              accept=".xls, .xlsx"
              label="Arraste ou clique aqui para importar sua planilha xls"
              onChange={([file]) => {
                if (file) setFile(file);
              }}
            />
          </div>
          <div className="row d-flex justify-between">
            <div className="col-sm-6 mt-s-200">
              <Input
                label="Arquivo selecionado"
                disabled
                placeholder={file?.name ?? 'Nenhum arquivo selecionado'}
              />
            </div>

            <div className="col-sm-3 mt-s-200">
              <Select
                label="Selecione o tipo"
                value={type?.label}
                placeholder="Selecione o tipo"
                onSelect={(type) => {
                  setType(type);
                }}
                options={[
                  { label: 'Receita', value: 'income' },
                  { label: 'Despesa', value: 'expense' },
                ]}
              />
            </div>

            <div className="col-sm-2 d-flex justify-end mt-s-550">
              <Button
                className="pl-s-700 pr-s-700"
                disabled={!file || !type}
                onClick={() => {
                  handleUpload();
                }}
              >
                Importar
              </Button>
            </div>
          </div>
          {uploading && (
            <div className="p-s-200">
              <Spinner />

              <div className="d-flex justify-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">
                    Importando planilha {file?.name}. Aguarde...
                  </span>
                </div>
              </div>
            </div>
          )}
          {result || uploadError ? (
            <div className="p-s-200">
              {result && (
                <>
                  <p className="d-flex justify-center text-success mb-s-200">
                    {result?.transactionsImportedCount}
                  </p>

                  <p className="d-flex justify-center text-warning mb-s-200">
                    {result?.transactionsNonImportedCount}
                  </p>
                </>
              )}

              {uploadError && (
                <div className="d-flex justify-center">
                  <span className="text-danger">
                    Erro ao realizar o upload de sua planilha, verifique se
                    todos os itens estão de acordo com as regras estabelecidadas
                    e tente novamente.
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </Modal>
        <Modal
          isOpen={isOpen}
          onClickAway={toggleTransactionModal}
          size="medium"
          title={watch('id') ? 'Editar realizado' : 'Adicionar realizado'}
          className="max-height-100 overflow-y-auto"
        >
          <form
            onSubmit={handleSubmit(
              (data) => {
                const transformedData = {
                  ...data,
                  paymentDate: new Date(
                    Date.parse(encodeDate(data.paymentDate))
                  ),
                };
                handleCreateOrUpdate(
                  transformedData as unknown as TransactionFile
                );
              },
              (_) => {
                toast.error('Erro ao cadastrar transação!', { type: 'error' });
              }
            )}
          >
            <div className="d-flex row gap-x-2 mb-s-200">
              <div className={watch('id') ? 'col-sm-6' : 'col-sm-12'}>
                <Select
                  label="Tipo"
                  value={getValue(watch('type'))}
                  error={!!errors.type}
                  caption={errors.type?.message as string}
                  placeholder="Selecione um tipo"
                  form={register('type', { required: 'Obrigatório' })}
                  onSelect={(options: any) => {
                    setValue('type', options.value);
                  }}
                  options={options.map((options) => {
                    return {
                      label: options.label,
                      value: options.value,
                    };
                  })}
                />
              </div>

              {watch('id') && (
                <div className="col-sm-6">
                  <Select
                    label="Centro de custo"
                    value={watch('costCenterName')}
                    error={!!errors.costCenterId}
                    caption={errors.costCenterId?.message as string}
                    placeholder="Selecione um centro de custo"
                    form={register('costCenterId', {
                      required: 'Obrigatório',
                    })}
                    onSelect={async (costCenter: any) => {
                      setValue('costCenterId', costCenter.value);
                      setValue('costCenterName', costCenter.label);
                      setValue('categoryId', '');
                      setValue('categoryName', 'Selecione uma categoria');
                      await loadCategories({
                        costCenterId: costCenter.value,
                        size: categoriesPageSize,
                      });
                    }}
                    options={costCenters.map((costCenter) => {
                      return {
                        label: costCenter.name,
                        value: costCenter.id,
                      };
                    })}
                  />
                </div>
              )}
            </div>
            {!watch('id') && (
              <div className="d-flex row gap-x-2 mb-s-200">
                <div className="col-sm-12">
                  <Select
                    label="Centro de custo de para"
                    value={costCenterValueLabel}
                    error={!!errors.costCenterFromToId}
                    caption={errors.costCenterFromToId?.message as string}
                    placeholder="Selecione um centro de custo"
                    form={register('costCenterFromToId', {
                      // Required only when creating. The field renders only in
                      // create mode, but its validator survives the unmount
                      // (the custom Select never propagates the ref cleanup),
                      // which silently blocked edits of transactions without a
                      // cost-center from/to (e.g. manually created income).
                      validate: (value) =>
                        Boolean(getValues('id')) ||
                        Boolean(value) ||
                        'Obrigatório',
                    })}
                    onSelect={async (costCenterFromToOption: {
                      value: string;
                      label: string;
                    }) => {
                      const costCenterFromTo = costCentersFromTo.find(
                        (costCenterFromTo) =>
                          costCenterFromTo.id === costCenterFromToOption.value
                      );
                      setValue(
                        'costCenterFromToId',
                        costCenterFromToOption.value
                      );
                      setValue('costCenterId', costCenterFromTo?.to.id);
                      setValue('categoryId', '');
                      setValue('categoryName', 'Selecione uma categoria');
                      await loadCategories({
                        costCenterId: costCenterFromTo?.to.id,
                        size: categoriesPageSize,
                      });
                    }}
                    options={costCentersFromTo.map((costCenterFromTo) => {
                      return {
                        label: `${costCenterFromTo.from} para ${costCenterFromTo.to.name}`,
                        value: costCenterFromTo.id,
                      };
                    })}
                  />
                </div>
              </div>
            )}

            <div className="d-flex row  gap-x-2 mb-s-200">
              <div className="col-sm-6">
                <Select
                  label="Categoria"
                  value={watch('categoryName')}
                  error={!!errors.categoryId}
                  caption={errors.categoryId?.message as string}
                  placeholder="Selecione uma categoria"
                  form={register('categoryId', { required: 'Obrigatório' })}
                  onSelect={(category: any) => {
                    setValue('categoryId', category.value);
                    setValue('categoryName', category.label);
                  }}
                  options={categories.map((category) => {
                    return {
                      label: category.name,
                      value: category.id,
                    };
                  })}
                  disabled={!watch('costCenterId')}
                />
              </div>
              <div className="col-sm-6">
                <Input
                  label="Descrição"
                  error={!!errors.description}
                  caption={errors.description?.message as string}
                  placeholder="Descrição"
                  form={register('description', { required: 'Obrigatório' })}
                />
              </div>
            </div>
            <div className="d-flex row  gap-x-2 mb-s-200">
              <div className="col-sm-6">
                <Input
                  label="Cliente"
                  error={!!errors.businessPartner}
                  caption={errors.businessPartner?.message as string}
                  placeholder="Digite um cliente"
                  form={register('businessPartner', {
                    required: 'Obrigatório',
                  })}
                />
              </div>
              <div className="col-sm-6">
                <Input
                  label="Montante"
                  error={!!errors.amount}
                  caption={errors.amount?.message as string}
                  placeholder="R$"
                  mask="currency"
                  form={register('amount', { required: 'Obrigatório' })}
                />
              </div>
            </div>
            <div className="d-flex row  gap-x-2">
              <div className="col-sm-6">
                <Input
                  label="Observação"
                  error={!!errors.observation}
                  caption={errors.observation?.message as string}
                  placeholder="Observação"
                  form={register('observation')}
                />
              </div>
              <div className="col-sm-6">
                <Input
                  mask="date"
                  label="Data do pagamento"
                  error={!!errors.paymentDate}
                  caption={errors.paymentDate?.message as string}
                  form={register('paymentDate', {
                    required: 'Obrigatório',
                    validate: (value: string) => {
                      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
                      if (!match) return 'Data inválida';
                      const [, day, month, year] = match.map(Number);
                      const date = new Date(year, month - 1, day);
                      const isReal =
                        date.getFullYear() === year &&
                        date.getMonth() === month - 1 &&
                        date.getDate() === day;
                      return isReal || 'Data inválida';
                    },
                  })}
                />
              </div>
            </div>
            <div className="d-flex justify-end pt-s-250">
              <Button className="pl-s-900 pr-s-900" type="submit">
                Salvar
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </>
  );
}
